import { useCallback, useEffect, useMemo, useState } from 'react'
import { Loader2, ChevronLeft, ChevronRight, Plus, Users } from 'lucide-react'
import { apiGet, cachedGet } from '../../lib/api.js'
import { apiErrorText } from '../../lib/apiError.js'
import { usePersistedState } from '../../lib/persist.js'
import { pickableBranches } from '../../lib/branch.js'
import {
  WEEK_HEAD, monthCells, monthRange, fmtMonthTH, thisMonth, shiftMonth,
  isSameMonth, todayKey, asList, relabelBranch, isShiftRow, rowDayKey,
} from '../../lib/doctorSchedule.js'
import { statusMeta, toneClass, shiftWindowLabel } from '../../lib/broadcast.js'
import { refreshSchedulePending } from '../../lib/schedulePending.js'
import ShiftRequestDrawer from '../../components/schedule/ShiftRequestDrawer.jsx'

/**
 * ขอเวร (/manager/schedule/doctor/shift-request) — asking doctors to fill an
 * empty shift.
 *
 * A second-level tab inside ตารางเวร › ทันตแพทย์, one click from the roster it
 * is trying to fill. RequireStaff matches the /api/hr/* proxies exactly, so no
 * extra tab gate is needed.
 *
 * Deliberately the SAME month grid as ตารางเวร › ทันตแพทย์: the helpers come from
 * lib/doctorSchedule.js, so the two calendars are one object rendered twice
 * rather than two that drift. Clicking an empty day opens the request drawer for
 * that day.
 *
 * 🚨 A shift request is NOT an `appointment` row and never becomes one (owner's
 * decision, 2026-08-20). Accepting a doctor records an outcome here; putting the
 * shift on the booking calendar stays a separate act in ตารางเวร › ทันตแพทย์,
 * which the drawer deep-links to. Nothing on this page writes to the roster.
 *
 * ⚠️ The roster IS read, purely as context — the faint "มีหมอแล้ว N" line under
 * each day comes from the same `/api/hr/schedule/doctor` feed the roster tab
 * uses. Without it the manager is asking for cover blind. It is a read; delete
 * it and the feature is unchanged.
 */

const SELECT = 'rounded-xl border border-slate-200 bg-white px-3 py-2 text-[13.5px] text-slate-700 outline-none focus:border-brand-400 focus:ring-4 focus:ring-brand-500/10'

export default function ShiftRequest() {
  const [anchor, setAnchor] = useState(thisMonth)
  const [branchId, setBranchId] = usePersistedState('shiftreq:branch', '')
  const [branches, setBranches] = useState([])
  const [rows, setRows] = useState([])
  const [roster, setRoster] = useState([])
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState('')
  const [selected, setSelected] = useState(null)     // day key for a NEW request
  const [openId, setOpenId] = useState(null)         // existing request id

  useEffect(() => {
    let alive = true
    cachedGet('/api/branches')
      .then((b) => { if (alive) setBranches(pickableBranches(asList(b))) })
      .catch(() => {})
    return () => { alive = false }
  }, [])

  const range = useMemo(() => monthRange(anchor), [anchor])

  const load = useCallback(async () => {
    setLoading(true)
    setErr('')
    try {
      const params = { start_date: range.start, end_date: range.end }
      if (branchId) params.branch_id = branchId
      const r = await apiGet('/api/hr/shift-request', params)
      setRows(Array.isArray(r?.items) ? r.items : [])
    } catch (e) {
      setErr(apiErrorText(e, 'โหลดคำขอเวรไม่สำเร็จ'))
    } finally {
      setLoading(false)
    }
  }, [range.start, range.end, branchId])

  useEffect(() => { load() }, [load])

  // Roster context. Requires a branch — Xano's timetable query has a branch
  // precondition, so there is no "ทุกสาขา" month, same as the roster tab.
  useEffect(() => {
    if (!branchId) { setRoster([]); return }
    let alive = true
    apiGet('/api/hr/schedule/doctor', { branch_id: branchId, start_date: range.start, end_date: range.end })
      .then((r) => { if (alive) setRoster(asList(r).filter(isShiftRow)) })
      .catch(() => { if (alive) setRoster([]) })
    return () => { alive = false }
  }, [branchId, range.start, range.end])

  const byDay = useMemo(() => {
    const m = new Map()
    for (const r of rows) {
      const k = String(r.date || '').slice(0, 10)
      if (!k) continue
      if (!m.has(k)) m.set(k, [])
      m.get(k).push(r)
    }
    return m
  }, [rows])

  const rosterByDay = useMemo(() => {
    const m = new Map()
    for (const r of roster) {
      const k = rowDayKey(r)
      if (!k) continue
      m.set(k, (m.get(k) || 0) + 1)
    }
    return m
  }, [roster])

  const cells = useMemo(() => monthCells(anchor), [anchor])
  const awaiting = rows.filter((r) => r.status === 'open' && Number(r.interested_count || 0) > 0).length

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="flex items-center gap-1">
          <button onClick={() => setAnchor((a) => shiftMonth(a, -1))} className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 transition-colors">
            <ChevronLeft size={16} />
          </button>
          <span className="text-[15px] font-semibold text-slate-900 min-w-[160px] text-center">{fmtMonthTH(anchor)}</span>
          <button onClick={() => setAnchor((a) => shiftMonth(a, 1))} className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 transition-colors">
            <ChevronRight size={16} />
          </button>
          {!isSameMonth(anchor, thisMonth()) && (
            <button onClick={() => setAnchor(thisMonth())} className="ml-1 text-[12.5px] text-brand-600 hover:text-brand-700">
              เดือนนี้
            </button>
          )}
        </div>

        <select className={`${SELECT} ml-auto`} value={branchId} onChange={(e) => setBranchId(e.target.value)}>
          <option value="">ทุกสาขา</option>
          {branches.map((b) => (
            <option key={b.id} value={b.id}>{relabelBranch(b.name)}</option>
          ))}
        </select>

        <button
          onClick={() => setSelected(todayKey())}
          className="inline-flex items-center gap-1.5 rounded-xl bg-brand-600 px-3 py-2 text-[13.5px] font-medium text-white hover:bg-brand-700 transition-colors"
        >
          <Plus size={15} strokeWidth={2} /> ขอเวร
        </button>
      </div>

      {awaiting > 0 && (
        <div className="mb-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5 flex items-center gap-2 text-[13px] text-amber-900">
          <Users size={14} strokeWidth={1.75} className="shrink-0 text-amber-600" />
          มีคำขอ {awaiting} รายการที่มีคุณหมอสนใจแล้ว รอเลือก
        </div>
      )}

      {err && <div className="mb-3 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2.5 text-[13px] text-rose-700">{err}</div>}

      {!branchId && (
        <p className="mb-3 text-[12.5px] text-slate-400">
          เลือกสาขาเพื่อดูว่าวันไหนมีหมอลงเวรอยู่แล้ว
        </p>
      )}

      <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
        <div className="grid grid-cols-7 border-b border-slate-200 bg-slate-50">
          {WEEK_HEAD.map((d) => (
            <div key={d} className="px-2 py-2 text-[12px] font-medium text-slate-500 text-center">{d}</div>
          ))}
        </div>
        <div className="grid grid-cols-7">
          {cells.map((key, i) => (
            <DayCell
              key={key || `pad-${i}`}
              dayKey={key}
              requests={key ? (byDay.get(key) || []) : []}
              rosterCount={key ? (rosterByDay.get(key) || 0) : 0}
              showRoster={Boolean(branchId)}
              onAdd={() => key && setSelected(key)}
              onOpen={(id) => setOpenId(id)}
            />
          ))}
        </div>
      </div>

      {loading && (
        <div className="flex items-center gap-2 text-[13px] text-slate-500 mt-3 justify-center">
          <Loader2 size={14} className="animate-spin" /> กำลังโหลด…
        </div>
      )}

      {(selected || openId != null) && (
        <ShiftRequestDrawer
          dayKey={selected}
          requestId={openId}
          branches={branches}
          defaultBranchId={branchId}
          onClose={() => { setSelected(null); setOpenId(null) }}
          // Same reason as การลา's onDecided: the badge has to move the moment the
          // manager acts, not up to two minutes later.
          onSaved={() => { setSelected(null); setOpenId(null); load(); refreshSchedulePending({ fresh: true }) }}
        />
      )}
    </div>
  )
}

function DayCell({ dayKey, requests, rosterCount, showRoster, onAdd, onOpen }) {
  if (!dayKey) return <div className="min-h-[104px] border-b border-r border-slate-100 bg-slate-50/40" />
  const isToday = dayKey === todayKey()
  const d = Number(dayKey.slice(8, 10))

  return (
    <div
      onClick={onAdd}
      className={`group min-h-[104px] border-b border-r border-slate-100 p-1.5 cursor-pointer transition-colors hover:bg-brand-50/40 ${
        isToday ? 'bg-brand-50/30' : ''
      }`}
    >
      <div className="flex items-center gap-1 mb-1">
        <span className={`text-[12px] ${isToday ? 'font-semibold text-brand-700' : 'text-slate-500'}`}>{d}</span>
        {/* Read-only roster context — never a link, never editable from here. */}
        {showRoster && rosterCount > 0 && (
          <span className="text-[10.5px] text-slate-400" title={`มีหมอลงเวร ${rosterCount} ท่าน`}>
            · หมอ {rosterCount}
          </span>
        )}
        <Plus size={11} className="ml-auto text-slate-300 opacity-0 group-hover:opacity-100 transition-opacity" />
      </div>

      <div className="space-y-0.5">
        {requests.map((r) => {
          const meta = statusMeta('shift_request', r.status)
          const interested = Number(r.interested_count || 0)
          return (
            <button
              key={r.id}
              onClick={(e) => { e.stopPropagation(); onOpen(r.id) }}
              className={`block w-full text-left text-[11px] leading-tight rounded border px-1 py-0.5 truncate transition ${toneClass(meta.tone)} hover:brightness-95`}
              title={`${shiftWindowLabel(r)} · ${meta.label}`}
            >
              <span className="font-semibold">{shiftWindowLabel(r)}</span>
              {r.status === 'open' && interested > 0 && (
                <span className="ml-1 opacity-80">· สนใจ {interested}</span>
              )}
              {r.status === 'closed' && <span className="ml-1 opacity-80">· ได้หมอแล้ว</span>}
            </button>
          )
        })}
      </div>
    </div>
  )
}
